import { useState, useEffect, useRef } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@/providers/Permissions.provider';
import { useClientData } from '@/providers/ClientData.provider';
import {} from '@/components/Form/types';
import { Button } from '@/components/UI';
import useClientAccounts from '@/hooks/useApi/useClientAccounts';
import useContacts from '@/hooks/useApi/useContacts';
import {
  Box,
  Icon,
  Select,
  FieldData,
  HighlightedText,
  ClientAccountBalance,
  formattedCurrency,
  withStyles,
} from 'vopay';
import styles from './TransactionCreationFields.module.scss';
const classes = withStyles(styles);
import { PaymentFieldDefault } from './types';

export const useContactFields = (
  contacts: {
    id: string;
    name: string;
    currency?: string;
    availableFunds?: number;
    balances?: ClientAccountBalance[];
  }[],
  onContactSearch: (val: string) => void,
  fetchingContacts: boolean,
): FieldData<PaymentFieldDefault>[] => {
  const {
    response: { clientAccountsEnabled, Country },
  } = useClientData();
  const { t } = useTranslation('translations', { keyPrefix: 'UseTransactionCreationFields' });
  const [isNewContact, setIsNewContact] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [contactName, setContactName] = useState('');
  const isFirstContactChange = useRef(true);

  const { permissions } = usePermissions();

  const { fetchClientAccount } = useClientAccounts();
  const { fetchContact } = useContacts();

  useEffect(() => {
    if (!fetchingContacts) {
      setIsLoading(fetchingContacts);
    }
  }, [fetchingContacts]);

  return [
    {
      label: () => t('Fields.clientAccount.label'),
      fieldName: 'Contact',
      getInputState: () => 'required',
      showField: () => clientAccountsEnabled,
      serializeField: (values) => (values.Contact === 'new' ? undefined : values.Contact),
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            filterable
            loading={isLoading}
            onFilter={(e) => {
              e.preventDefault();
              setIsLoading(true);

              e.debounce(() => {
                if (!e.filter || e.filter.length >= 3) {
                  onContactSearch(e.filter);
                }
              }, 1000);
            }}
            // Deduplicate: ensure each currency wallet appears only once per account.
            options={contacts.filter((o, i, arr) => arr.findIndex((x) => x.id === o.id) === i)}
            optionValue={(o) => o.id}
            renderOption={(o, { Option, SelectedIcon, selected, preventDefault, disabled, filter }) => {
              const currency = ctx.values.Currency;
              const wallet = contacts.find((c) => c.id === o.id && c.currency === currency);
              const balanceAccount = wallet?.availableFunds ?? 0;

              return (
                <Option
                  onClick={() => {
                    if (disabled) {
                      preventDefault();
                    } else {
                      setIsNewContact(false);
                    }
                  }}
                >
                  <div>
                    <HighlightedText text={o.name} searchTerm={filter} />
                    <div className={classes('availableBalance')}>
                      {t('Fields.availableBalance', {
                        currency,
                        balance: formattedCurrency(balanceAccount.toString()),
                        defaultValue: `(Available Bal. ${currency} $${formattedCurrency(balanceAccount.toString())})`,
                      })}
                    </div>
                  </div>
                  {selected && <SelectedIcon />}
                </Option>
              );
            }}
            renderSelectedValue={(_data, { Placeholder }) => {
              if (!contactName) return <Placeholder />;

              return contactName;
            }}
            placeholder={t('Fields.clientAccount.placeholder')}
            {...fieldData}
            onChange={async (e, obj) => {
              fieldData.onChange(e);
              // Only reset payment fields on subsequent changes, not the first time
              if (!isFirstContactChange.current) {
                ctx.setFieldTouched('PaymentMethod', false);
                ctx.setFieldValue('PaymentMethod', '');
                ctx.setFieldTouched('PaymentSelectionMethod', false);
                ctx.setFieldValue('PaymentSelectionMethod', '');
              }
              isFirstContactChange.current = false;
              ctx.setFieldTouched('Amount', false);
              ctx.setFieldValue('Amount', '0.00');
              ctx.setFieldValue('SecCode', 'PPD');
              ctx.setFieldTouched('SecCode', false);
              if (obj.value === 'new') return;

              if (!clientAccountsEnabled) return;
              if (!permissions.ViewClientAccounts) {
                setContactName(obj.data?.name || '');
                return;
              }
              const account = await fetchClientAccount(obj.value);

              setContactName(account?.ClientName || obj.data?.name || '');

              if (!account) return;
              const secCode = account.IsBusiness ? 'CCD' : 'PPD';
              ctx.setFieldValue('SecCode', secCode);
              ctx.setFieldTouched('SecCode', false);
              ctx.setFieldTouched('IdentificationNumber', false);
              ctx.setFieldValue('IdentificationNumber', account.IdentificationNumber);
              ctx.setFieldValue('RecipientPhoneNumber', account.PhoneNumber || '');
              ctx.setFieldValue('ReceiverPhoneNumber', account.PhoneNumber || '');
              ctx.setFieldValue('ReceiverEmailAddress', account.EmailAddress || '');
              ctx.setFieldValue('OverrideEmailAddress', account.EmailAddress || '');

              ctx.setFieldValue('ClientAccountPhoneNumber', account.PhoneNumber || '');
              ctx.setFieldValue('Address1', account.Address1 || '');
              ctx.setFieldValue('Address2', account.Address2 || '');
              ctx.setFieldValue('City', account.City || '');
              ctx.setFieldValue('Province', account.Province || '');
              ctx.setFieldValue('PostalCode', account.PostalCode || '');
              ctx.setFieldValue('Country', account.Country || Country || '');
            }}
          />
        );
      },

      validate: () => Yup.mixed().required(),
    },
    {
      label: () => t('Fields.contact.label'),
      fieldName: 'Contact',
      getInputState: () => 'required',
      showField: () => !clientAccountsEnabled,
      serializeField: (values) => (values.Contact === 'new' ? undefined : values.Contact),
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            filterable
            loading={isLoading}
            onFilter={(e) => {
              e.preventDefault();
              setIsLoading(true);

              e.debounce(() => {
                if (!e.filter || e.filter.length >= 3) {
                  onContactSearch(e.filter);
                }
              }, 1000);
            }}
            options={contacts}
            optionValue={(o) => o.id}
            renderOption={(o, { Option, SelectedIcon, selected, preventDefault, disabled, filter }) => (
              <Option
                onClick={() => {
                  if (disabled) {
                    preventDefault();
                  } else {
                    setIsNewContact(false);
                  }
                }}
              >
                <div>
                  <HighlightedText text={o.name} searchTerm={filter} />
                </div>
                {selected && <SelectedIcon />}
              </Option>
            )}
            renderSelectedValue={(_data, { Placeholder }) => {
              if (isNewContact) return t('Fields.contact.addNewContactLabel');

              if (!contactName) return <Placeholder />;

              return contactName;
            }}
            renderDropdownFooter={({ close }) => (
              <Box flx="col">
                {permissions.ManageContacts && (
                  <Button
                    colorScheme="secondary"
                    onClick={() => {
                      setIsNewContact(true);
                      ctx.setFieldValue('Contact', 'new');
                      close();
                    }}
                  >
                    <Box flx="row" gapSize="8px" jc="center" ai="center">
                      <Icon height="20px" width="20px" icon="add" color="medium-dark" />
                      <span>{t('Fields.contact.addNewContactButton')}</span>
                    </Box>
                  </Button>
                )}
              </Box>
            )}
            placeholder={t('Fields.contact.placeholder')}
            {...fieldData}
            onChange={async (e, obj) => {
              fieldData.onChange(e);
              if (obj.value === 'new') {
                ctx.setFieldValue('Contact', obj.value);
                ctx.setFieldValue('SecCode', 'PPD');
                ctx.setFieldValue('PaymentMethod', 'new');
                ctx.setFieldValue('PaymentSelectionMethod', '');
                ctx.setFieldValue('EtransferConnectionType', 'email');
                ctx.setFieldValue('Country', Country || '');
                ctx.setFieldValue('Answer', '');
                ctx.setFieldValue('ClientReferenceNumber', '');
                ctx.setFieldValue('Question', '');
                ctx.setFieldValue('RecipientEmailAddress', '');
                ctx.setFieldValue('ReceiverEmailAddress', '');
                ctx.setFieldValue('RecipientName', '');
                ctx.setFieldValue('RecipientPhoneNumber', '');
                ctx.setFieldValue('ReceiverPhoneNumber', '');
                ctx.setFieldValue('FinancialInstitutionNumber', '');
                ctx.setFieldValue('BranchTransitNumber', '');
                ctx.setFieldValue('ABARoutingNumber', '');
                ctx.setFieldValue('AccountNumber', '');
                ctx.setFieldValue('Address1', '');
                ctx.setFieldValue('City', '');
                ctx.setFieldValue('Province', '');
                ctx.setFieldValue('PostalCode', '');
                ctx.setFieldValue('FirstName', '');
                ctx.setFieldValue('LastName', '');
                ctx.setFieldValue('CompanyName', '');
                ctx.setFieldValue('BankPhoneNumber', '');
                ctx.setFieldTouched('BankPhoneNumber', false);
                ctx.setFieldTouched('Answer', false);
                ctx.setFieldTouched('ClientReferenceNumber', false);
                ctx.setFieldTouched('Question', false);
                ctx.setFieldTouched('RecipientEmailAddress', false);
                ctx.setFieldTouched('RecipientName', false);
                ctx.setFieldTouched('RecipientPhoneNumber', false);
                ctx.setFieldTouched('ReceiverPhoneNumber', false);
                ctx.setFieldTouched('ReceiverEmailAddress', false);
                ctx.setFieldTouched('FinancialInstitutionNumber', false);
                ctx.setFieldTouched('BranchTransitNumber', false);
                ctx.setFieldTouched('ABARoutingNumber', false);
                ctx.setFieldTouched('AccountNumber', false);
                ctx.setFieldTouched('Address1', false);
                ctx.setFieldTouched('City', false);
                ctx.setFieldTouched('Province', false);
                ctx.setFieldTouched('Country', false);
                ctx.setFieldTouched('PostalCode', false);
                ctx.setFieldTouched('FirstName', false);
                ctx.setFieldTouched('LastName', false);
                ctx.setFieldTouched('CompanyName', false);
                ctx.setFieldTouched('SecCode', false);
              } else {
                // Fetch the full contact details to get address
                let contactAddress1 = '';
                let contactAddress2 = '';
                let contactCity = '';
                let contactProvince = '';
                let contactPostalCode = '';
                let contactCountry = '';
                let _contactName = obj.data?.name || '';
                let contactEmail = '';
                let contactPhone = '';
                let secCode = 'PPD';
                try {
                  const contact = await fetchContact(obj.value);
                  if (contact) {
                    contactAddress1 = contact.Address1 || '';
                    contactAddress2 = contact.Address2 || '';
                    contactCity = contact.City || '';
                    contactProvince = contact.Province || '';
                    contactPostalCode = contact.PostalCode || '';
                    contactCountry = contact.Country || Country || '';
                    _contactName = contact.ContactName || _contactName;
                    contactEmail = contact.EmailAddress || contactEmail;
                    contactPhone = contact.PhoneNumber || contactPhone;
                    if (contact.IsBusiness) {
                      secCode = 'CCD';
                    }
                  }
                } catch (err) {
                  console.error('Error fetching contact details:', err);
                }
                setContactName(_contactName);
                // Only reset payment fields on subsequent changes, not the first time to preserve tokens
                const shouldResetPaymentFields = !isFirstContactChange.current;
                isFirstContactChange.current = false;

                // Changed method because of ctx.values having old values and causing conflicting updates and overwritting when onchange is called on initial feilds
                ctx.setFieldValue('PaymentMethod', shouldResetPaymentFields ? '' : ctx.values.PaymentMethod || '');
                ctx.setFieldValue(
                  'PaymentSelectionMethod',
                  shouldResetPaymentFields ? '' : ctx.values.PaymentSelectionMethod || '',
                );
                ctx.setFieldValue('Contact', obj.value);
                ctx.setFieldValue('EtransferConnectionType', 'email');
                ctx.setFieldValue('ProvideOrRequestPaymentInformation', 'provide');
                ctx.setFieldValue('Country', contactCountry || Country || '');
                ctx.setFieldValue('Amount', '');
                ctx.setFieldValue('Answer', '');
                ctx.setFieldValue('ClientReferenceNumber', '');
                ctx.setFieldValue('Question', '');
                ctx.setFieldValue('RecipientEmailAddress', contactEmail);
                ctx.setFieldValue('OverrideEmailAddress', contactEmail);
                ctx.setFieldValue('RecipientName', _contactName || '');
                ctx.setFieldValue(
                  'RecipientPhoneNumber',
                  shouldResetPaymentFields ? '' : ctx.values.RecipientPhoneNumber || '',
                );
                ctx.setFieldValue('ReceiverEmailAddress', contactEmail);
                ctx.setFieldValue('ReceiverPhoneNumber', contactPhone);
                ctx.setFieldValue('FinancialInstitutionNumber', '');
                ctx.setFieldValue('BranchTransitNumber', '');
                ctx.setFieldValue('ABARoutingNumber', '');
                ctx.setFieldValue('AccountNumber', '');
                ctx.setFieldValue('Address1', contactAddress1);
                ctx.setFieldValue('Address2', contactAddress2);
                ctx.setFieldValue('City', contactCity);
                ctx.setFieldValue('Province', contactProvince);
                ctx.setFieldValue('PostalCode', contactPostalCode);

                ctx.setFieldValue('FirstName', '');
                ctx.setFieldValue('LastName', '');
                ctx.setFieldValue('CompanyName', '');

                ctx.setFieldValue('SecCode', secCode);
                ctx.setFieldTouched('SecCode', false);

                ctx.setFieldTouched('Amount', false);
                ctx.setFieldTouched('Answer', false);
                ctx.setFieldTouched('ClientReferenceNumber', false);
                ctx.setFieldTouched('Question', false);
                ctx.setFieldTouched('RecipientEmailAddress', false);
                ctx.setFieldTouched('RecipientName', false);
                ctx.setFieldTouched('RecipientPhoneNumber', false);
                ctx.setFieldTouched('ReceiverPhoneNumber', false);
                ctx.setFieldTouched('ReceiverEmailAddress', false);
                ctx.setFieldTouched('FinancialInstitutionNumber', false);
                ctx.setFieldTouched('BranchTransitNumber', false);
                ctx.setFieldTouched('ABARoutingNumber', false);
                ctx.setFieldTouched('AccountNumber', false);
                ctx.setFieldTouched('Address1', false);
                ctx.setFieldTouched('City', false);
                ctx.setFieldTouched('Province', false);
                ctx.setFieldTouched('Country', false);
                ctx.setFieldTouched('PostalCode', false);
                ctx.setFieldTouched('FirstName', false);
                ctx.setFieldTouched('LastName', false);
                ctx.setFieldTouched('CompanyName', false);
                ctx.setFieldTouched('ProvideOrRequestPaymentInformation', false);
                ctx.setFieldTouched('PaymentMethod', false);
                ctx.setFieldTouched('PaymentSelectionMethod', false);

                const nameParts = _contactName.trim().split(' ') || [];

                const isCompanyName = nameParts.length > 2 || nameParts.length < 2;
                if (isCompanyName) {
                  ctx.setFieldValue('CompanyName', nameParts.join(' ') || '');
                } else {
                  ctx.setFieldValue('FirstName', nameParts[0] || '');
                  ctx.setFieldValue('LastName', nameParts.slice(1).join(' ') || '');
                }
              }
            }}
          />
        );
      },

      validate: () => Yup.mixed().required(),
    },
  ];
};
